import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import SearchInput from '@/components/common/SearchInput';
import Select from '@/components/common/Select';
import { supabase } from '@/services/supabase';
import { useAuthStore } from '@/stores/authStore';
import {
  BellAlertIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// All notification types supported by the send-notification edge function
const NOTIFICATION_TYPES = [
  { value: 'system_announcement', label: 'System Announcement' },
  { value: 'new_message', label: 'New Message' },
  { value: 'incoming_call', label: 'Incoming Call' },
  { value: 'connection_request', label: 'Connection Request' },
  { value: 'connection_accepted', label: 'Connection Accepted' },
  { value: 'recommendation_match', label: 'Recommendation Match' },
  { value: 'network_update', label: 'Network Update' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'business_inquiry', label: 'Business Inquiry' },
  { value: 'lead_update', label: 'Lead Update' },
  { value: 'review_received', label: 'Review Received' },
  { value: 'profile_view', label: 'Profile View' },
  { value: 'subscription_expiring', label: 'Subscription Expiring' },
  { value: 'employee_invitation', label: 'Employee Invitation' },
  { value: 'employee_invitation_accepted', label: 'Employee Invitation Accepted' },
  { value: 'employee_invitation_declined', label: 'Employee Invitation Declined' },
  { value: 'employee_left_business', label: 'Employee Left Business' },
];

interface User {
  user_id: string;
  user_email: string;
  full_name: string | null;
  user_phone_number: string | null;
}

interface PushToken {
  token: string;
  platform: string;
  device_name: string | null;
  is_active: boolean;
  updated_at: string;
}

interface SendNotificationResponse {
  success: boolean;
  notification_id?: string;
  push_sent?: boolean;
  push_skipped_reason?: string;
  error?: string;
}

export default function TestNotificationPage() {
  const { user: adminUser } = useAuthStore();
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [notificationType, setNotificationType] = useState('system_announcement');
  const [title, setTitle] = useState('Test Notification');
  const [body, setBody] = useState('This is a test notification from the admin console.');
  const [forcePush, setForcePush] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<SendNotificationResponse | null>(null);

  // Search users
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['user-search', search],
    queryFn: async () => {
      if (!search || search.length < 2) return [];

      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, user_email, full_name, user_phone_number')
        .or(`user_email.ilike.%${search}%,full_name.ilike.%${search}%,user_phone_number.ilike.%${search}%`)
        .limit(10);

      if (error) throw error;
      return data as User[];
    },
    enabled: search.length >= 2 && !selectedUser,
  });

  // Get push token for selected user
  const { data: pushToken, isLoading: tokenLoading } = useQuery({
    queryKey: ['push-token', selectedUser?.user_id],
    queryFn: async () => {
      if (!selectedUser) return null;

      const { data, error } = await supabase
        .from('push_tokens')
        .select('token, platform, device_name, is_active, updated_at')
        .eq('user_id', selectedUser.user_id)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as PushToken | null;
    },
    enabled: !!selectedUser,
  });

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setSearch('');
    setLastResult(null);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setLastResult(null);
  };

  const handleSendNotification = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSending(true);
    setLastResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          recipient_user_id: selectedUser.user_id,
          notification_type: notificationType,
          title: title.trim(),
          body: body.trim() || undefined,
          force_push: forcePush,
          send_push: true,
          metadata: {
            test: true,
            sent_by_admin: adminUser?.id,
            sent_from: 'admin_console',
          },
        },
      });

      if (error) throw error;

      setLastResult(data as SendNotificationResponse);

      if (data.success) {
        if (data.push_sent) {
          toast.success('Notification sent successfully!');
        } else {
          toast.success(`Notification created (push skipped: ${data.push_skipped_reason || 'unknown'})`);
        }
      } else {
        toast.error(data.error || 'Failed to send notification');
      }
    } catch (err) {
      console.error('Failed to send notification:', err);
      const message = err instanceof Error ? err.message : 'Failed to send notification';
      toast.error(message);
      setLastResult({ success: false, error: message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Test Push Notifications</h1>
        <p className="mt-1 text-sm text-gray-500">
          Send test push notifications to any user to verify the notification system is working.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column - Form */}
        <div className="space-y-6">
          {/* User Selection */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">Select User</h2>
            </CardHeader>
            <CardBody>
              {selectedUser ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand font-medium">
                      {selectedUser.full_name?.[0]?.toUpperCase() || selectedUser.user_email[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedUser.full_name || 'No name'}
                      </p>
                      <p className="text-sm text-gray-500">{selectedUser.user_email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClearUser}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder="Search by name, email, or phone..."
                  />

                  {searchLoading && (
                    <p className="text-sm text-gray-500">Searching...</p>
                  )}

                  {searchResults && searchResults.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user.user_id}
                          onClick={() => handleSelectUser(user)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                            {user.full_name?.[0]?.toUpperCase() || user.user_email[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user.full_name || 'No name'}
                            </p>
                            <p className="text-xs text-gray-500">{user.user_email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {search.length >= 2 && !searchLoading && searchResults?.length === 0 && (
                    <p className="text-sm text-gray-500">No users found</p>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Push Token Status */}
          {selectedUser && (
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900">Push Token Status</h2>
              </CardHeader>
              <CardBody>
                {tokenLoading ? (
                  <p className="text-sm text-gray-500">Checking push token...</p>
                ) : pushToken ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircleIcon className="w-5 h-5" />
                      <span className="font-medium">Push token registered</span>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>Platform: <span className="font-medium">{pushToken.platform}</span></p>
                      {pushToken.device_name && (
                        <p>Device: <span className="font-medium">{pushToken.device_name}</span></p>
                      )}
                      <p>Last updated: <span className="font-medium">
                        {new Date(pushToken.updated_at).toLocaleString()}
                      </span></p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600">
                    <XCircleIcon className="w-5 h-5" />
                    <span className="font-medium">No push token registered</span>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Notification Settings */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <Select
                  label="Notification Type"
                  value={notificationType}
                  onChange={setNotificationType}
                  options={NOTIFICATION_TYPES}
                />

                <div>
                  <label htmlFor="test-notification-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    id="test-notification-title"
                    name="test_notification_title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    placeholder="Notification title"
                    aria-label="Notification title"
                  />
                </div>

                <div>
                  <label htmlFor="test-notification-body" className="block text-sm font-medium text-gray-700 mb-1">
                    Body (optional)
                  </label>
                  <textarea
                    id="test-notification-body"
                    name="test_notification_body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    placeholder="Notification body text"
                    aria-label="Notification body"
                  />
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="force_push"
                    checked={forcePush}
                    onChange={(e) => setForcePush(e.target.checked)}
                    className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
                    aria-label="Force push notification"
                  />
                  <span className="text-sm text-gray-700">
                    Force Push (bypass preferences & quiet hours)
                  </span>
                </label>

                <button
                  onClick={handleSendNotification}
                  disabled={!selectedUser || sending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <BellAlertIcon className="w-5 h-5" />
                  {sending ? 'Sending...' : 'Send Test Notification'}
                </button>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Right column - Result & Info */}
        <div className="space-y-6">
          {/* Last Result */}
          {lastResult && (
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900">Result</h2>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <div className={`flex items-center gap-2 ${lastResult.success ? 'text-green-600' : 'text-red-600'}`}>
                    {lastResult.success ? (
                      <CheckCircleIcon className="w-5 h-5" />
                    ) : (
                      <XCircleIcon className="w-5 h-5" />
                    )}
                    <span className="font-medium">
                      {lastResult.success ? 'Notification sent' : 'Failed to send'}
                    </span>
                  </div>

                  {lastResult.success && (
                    <div className="text-sm text-gray-600 space-y-1">
                      {lastResult.notification_id && (
                        <p>Notification ID: <code className="bg-gray-100 px-1 rounded">{lastResult.notification_id}</code></p>
                      )}
                      <p>Push sent: <span className="font-medium">{lastResult.push_sent ? 'Yes' : 'No'}</span></p>
                      {lastResult.push_skipped_reason && (
                        <p>Skip reason: <span className="font-medium">{lastResult.push_skipped_reason}</span></p>
                      )}
                    </div>
                  )}

                  {lastResult.error && (
                    <p className="text-sm text-red-600">{lastResult.error}</p>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Info */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">How It Works</h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4 text-sm text-gray-600">
                <div className="flex gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                  <p>
                    This tool sends notifications through the same <code className="bg-gray-100 px-1 rounded">send-notification</code> edge function used by the app.
                  </p>
                </div>

                <div className="flex gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Force Push</strong> bypasses the user's notification preferences and quiet hours settings.
                  </p>
                </div>

                <div className="flex gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                  <p>
                    Notifications require a <strong>physical device</strong> with the app installed. Simulators do not receive push notifications.
                  </p>
                </div>

                <div className="flex gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                  <p>
                    Different notification types may navigate to different screens when tapped.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
